import { sanityClient, hasSanity } from "./sanityClient";

/**
 * Article reads.
 *
 * Shaped like `systemsApi.js`: plain async functions returning data, with the
 * caching and loading state left to the React Query hooks above them.
 *
 * Every query here reads the **published** perspective, which is the default
 * and is deliberately not overridden. All twelve migrated articles are Sanity
 * drafts, so these return nothing until a human presses Publish. That is the
 * intended behaviour, not a bug to work around: there is no `status` field to
 * filter on and therefore no filter anyone can forget.
 */

/** Everything the index needs. No `body` — it is large and unused there. */
const INDEX_FIELDS = `
  _id,
  title,
  "slug": slug.current,
  category,
  tags,
  excerpt,
  featured,
  publishedAt,
  "author": author->{name, role}
`;

const ARTICLE_FIELDS = `
  ${INDEX_FIELDS},
  body,
  sources[]{label, url},
  factsCheckedOn
`;

/**
 * Published articles, newest first.
 *
 * Ordered by `publishedAt` descending with `_createdAt` as the tiebreak, so a
 * batch published in one sitting — all sharing a timestamp to the second —
 * still has a stable order rather than shuffling between requests.
 */
export const getPublishedArticles = async () => {
  if (!hasSanity) return [];
  return sanityClient.fetch(
    `*[_type == "article" && defined(slug.current)]
       | order(publishedAt desc, _createdAt desc)
       { ${INDEX_FIELDS} }`,
  );
};

/** One article by slug, or null if there is no published article at that slug. */
export const getArticleBySlug = async (slug) => {
  if (!hasSanity || !slug) return null;
  return sanityClient.fetch(
    `*[_type == "article" && slug.current == $slug][0]{ ${ARTICLE_FIELDS} }`,
    { slug },
  );
};

/** Slugs only — for the build-time prerender step. */
export const getPublishedSlugs = async () => {
  if (!hasSanity) return [];
  return sanityClient.fetch(
    `*[_type == "article" && defined(slug.current)].slug.current`,
  );
};
